/* eslint-disable max-lines */
import React, {Component, ReactNode} from 'react';
import {inject, observer} from 'mobx-react';
import {
    CasinoGamesStoreInterface,
    CasinoGamesStoreProps
} from 'app/interfaces/stores/CasinoGamesStoreInterfaces';
import CasinoGameItem from './CasinoGameItem';
import EmptyGames from './EmptyGames';
import I18n from 'app/i18n';
import {UserStore} from 'app/store/UserStore';
import SmartLoader from 'app/components/reusable/SmartLoader';
import {showCasinoSignUp} from 'app/utils/IsRegisterUser';
import FirstLevelSort from './FirstLevelSort';
import FilterGroups from './FilterGroups';
import CasinoLootboxes from './CasinoLootboxes';
import {isDesktop, isGuest, getLinkPath, scrollToTop} from 'app/utils';
import DisabledCasino from './DisabledCasino';
import ThirdLevelSort from './ThirdLevelSort';
import BarLoginMobile from 'app/components/auth/BarLoginMobile';
import {ELEMENT_NOT_FOUND, KEY_ENTER, CASINO_TAB_LOOTBOX} from 'app/utils/Consts';
import {LazyLoading} from 'app/utils/lazyLoading/LazyLoading';
import {ScrollUp} from 'app/components/ScrollUp';
import {EgbButton} from 'app/components/Button';
import IframeView from './iframe-view/IframeView';
import OptionInterface from 'app/interfaces/OptionInterface';
import {size190x190} from 'app/utils/CasinoGameImages';
import {LiveCreatableSelect} from 'app/components/reusable/LiveCreatableSelect';
import {startCasinoGame} from 'app/services/CasinoServices';
import SelectPicker from 'app/components/reusable/SelectPicker';
import {goToRoute} from 'app/utils/link_helper';
import CollectionInterface from 'app/interfaces/CollectionInterface';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';
import PopularGamesCarousel from 'app/components/pages/casino/PopularGamesCarousel';
import CasinoMarketingBlocks from 'app/components/pages/casino/CasinoMarketingBlocks';
import {CASINO_ATTRS} from 'app/components/pages/casino/marketing-blocks/CasinoMarketingUtils';
import {isCoolOffActiveFor} from 'app/utils/isCoolOffActiveFor';

const RADIX = 36;
const KEY_LENGTH = 7;
const HEIGHT_FILTER = 300;
const ALL_GAMES_FILTER_ID = -1;

interface CasinoStateInterface {
    activeClickGame: number | null
    currency: string
    findValue: string
    gameActive: boolean
    showWidget?: boolean
    key: string
    gameId: string | null
    isLootboxDetailPage: boolean
    searchInputValue: string
    savedFindValue: string
}

interface UserStoreProps {
    userStore?: UserStore;
}

@inject('casinoGamesStore', 'userStore')
@observer
export default class Casino extends Component<CasinoGamesStoreProps & UserStoreProps, CasinoStateInterface> {
    constructor(props: CasinoGamesStoreProps) {
        super(props);
        this.state = {
            activeClickGame: null,
            currency: 'usd',
            findValue: '',
            gameActive: false,
            gameId: null,
            isLootboxDetailPage: false,
            key: this.randomKey(),
            savedFindValue: '',
            searchInputValue: '',
            showWidget: false
        };
    }

    rootElement: React.RefObject<HTMLDivElement> = React.createRef();

    popularGamesRef: React.RefObject<HTMLDivElement> = React.createRef();

    casinoFilterRef: React.RefObject<HTMLDivElement> = React.createRef();

    iframeGridRef: React.RefObject<HTMLDivElement> = React.createRef();

    disableScrollRestoration = (): void => {
        if ('scrollRestoration' in window.history) {
            (window.history as History & {scrollRestoration?: 'auto' | 'manual'}).scrollRestoration = 'manual';
        }
    };

    setupScrollListener = (): void => {
        if (!isDesktop()) {
            window.addEventListener('scroll', this.handleScroll);
        }
    };

    initializeOnMount = (): void => {
        const gameId = this.getGameId();
        const {updateGameId, initialize} = this.props.casinoGamesStore;
        const {pathname, search} = window.location;

        if (pathname.includes('casino') && (pathname.includes('lootboxes') || search.includes('currency'))) {
            window.location.href = getLinkPath('/casino');
            return;
        }

        if (isGuest()) {
            if (gameId) {
                updateGameId(gameId);
            }
            initialize();
        } else {
            initialize();
            if (gameId) {
                this.openGame(Number(gameId));
            }
        }
        this.setupScrollListener();
        this.loadGamesIfEmpty();
    };

    componentDidMount(): void {
        this.disableScrollRestoration();
        this.initializeOnMount();
    }

    openGame = (gameId: number): void => {
        startCasinoGame(Number(gameId));
    };

    getGameId = (): string | null => {
        const params = new URLSearchParams(window.location.search);
        const gameId = params.get('id');

        if (params && gameId) {
            return gameId;
        }
        return null;
    };

    componentWillUnmount(): void {
        this.props.casinoGamesStore.closeModal();
        window.removeEventListener('scroll', this.handleScroll);
    }

    handleScroll = (event: Event): void => {
        const blockWidth = -400;
        const target = event.target as Document;
        const {top} = target.body.getBoundingClientRect();

        if (top <= blockWidth) {
            this.setState({showWidget: true});
        } else {
            this.setState({showWidget: false});
        }
    };

    loadGamesIfEmpty = (): void => {
        const {
            casinoGames
        } = this.props.casinoGamesStore.data;

        if (casinoGames.length === 0) {
            this.props.casinoGamesStore.loadCasinoGames();
        }
    };

    favoriteFilter = (): void => {
        const {casinoGamesStore} = this.props;
        const {showOnlyFavorite} = casinoGamesStore;

        if (showCasinoSignUp()) {
            return;
        }
        if (!showOnlyFavorite) {
            this.clearSearch();
        }
        casinoGamesStore.clearFind();
        casinoGamesStore.updateShowOnlyFavorite(!showOnlyFavorite);
        casinoGamesStore.updateFilteredParams('favorite', !showOnlyFavorite);
        casinoGamesStore.clearSelectedCategories();
        this.scrollToFilter(true);
    };

    selectedValue = (selected?: OptionInterface | OptionInterface[] | null): string => Array.isArray(selected)
        ? selected.find(Boolean).label
        : selected.label;

    changSearchData = (selected?: OptionInterface | OptionInterface[] | null): void => {
        scrollToTop();
        setTimeout(() => this.scrollToFilter(), 100);

        if (!selected) {
            this.clearSearch(this.updateFilteredParams);
            return;
        }

        const identifier = 'identifier';
        const value = 'value';
        const gameId = selected[identifier] && selected[value] ? selected[value].toString() : null;
        const selectedValue = this.selectedValue(selected);
        const findValue = gameId ? selectedValue.replace(/\s*$/u, '') : selectedValue;
        this.setState({findValue, gameId}, () => {
            this.updateFilteredParams();
        });
    };

    clearSearch = (callback?: () => void): void => {
        const state = {
            findValue: '',
            gameId: null,
            key: this.randomKey(),
            savedFindValue: '',
            searchInputValue: ''
        };

        if (typeof callback === 'function') {
            this.setState(state, callback);
        } else {
            this.setState(state);
        }

        this.scrollToFilter(true);
    };

    handleSearchData = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === KEY_ENTER) {
            this.props.casinoGamesStore.updateShowOnlyFavorite(false);
            this.updateFilteredParams();
        }
    };

    updatedSelect = (): void => {
        this.setState({key: this.randomKey()});
    };

    clearFilters = (): void => {
        this.clearSearch(() => {
            this.props.casinoGamesStore.updateShowOnlyFavorite(false);
            this.updateFilteredParams();
            this.loadGamesIfEmpty();
            this.props.casinoGamesStore.changeFilterGroup(ELEMENT_NOT_FOUND);
        });
    };

    handleGoToTournamentGames = (filterGroupId: number): void => {
        const {casinoGamesStore} = this.props;

        casinoGamesStore.changeFilterGroup(filterGroupId);
        this.clearSearch();
    };

    updateFilteredParams = (): void => {
        const {casinoGamesStore} = this.props;

        if (showCasinoSignUp()) {
            return;
        }
        casinoGamesStore.updateFindFilteredParams(this.state.findValue, this.state.gameId);
    };

    setActiveGame = (id: number): void => {
        this.setState({activeClickGame: id});
    };

    deactivateGame = (): void => {
        this.setState({activeClickGame: null});
    };

    loadMoreGames = (): void => {
        this.props.casinoGamesStore.loadCasinoGames();
    };

    showScrollUp = (): React.ReactNode => !isDesktop() && !isGuest() && <ScrollUp
        show={this.state.showWidget}
        behavior={'instant'}
        icon={<EgbButton name={'back_to_top'}/>}
        element={this.rootElement.current}/>;


    loadOptions = (inputValue: string): void | Promise<OptionInterface[]> => this.props.casinoGamesStore.liveSearchApiService(inputValue)
        .then<CasinoGamesStoreInterface>(response => response.json())
        .then(data => data.casinoGames.map(game => ({
            identifier: game.identifier,
            image_url: game.image_url,
            in_out: game.in_out,
            label: `${game.name}  `,
            provider_name: game.provider_name,
            provider_title: game.provider_title,
            released_at: game.released_at,
            value: game.id
        })));

    /* eslint-disable  @typescript-eslint/explicit-module-boundary-types */
    customOption = ({innerProps, data}): ReactNode => <div className="games_filter__menu-item" {...innerProps}>
        <span className="games_filter__menu-item__game-logo">
            {data.provider_name && data.identifier && <img
                alt={data.provider_name}
                src={size190x190(
                    data.provider_name,
                    data.identifier,
                    data.released_at,
                    data.in_out,
                    data.image_url
                )}/>
            }
        </span>
        <div className="games_filter__menu-item__game">
            <span className="games_filter__menu-item__game-name">
                {data.label}
            </span>
            <span className="games_filter__menu-item__game-provider">
                {data.provider_title || data.provider_name}
            </span>
        </div>
    </div>;

    randomKey = ():string => (Math.random() + 1).toString(RADIX).substring(KEY_LENGTH);

    get newCurrencies() {
        return [{text: 'USD', value: 'usd'}, {text: I18n.t('user_points'), value: 'points'}];
    }

    get currencies() {
        return this.newCurrencies.map((currency): CollectionInterface => ({
            text: currency.text,
            value: currency.value
        }));
    }

    handleChangeCurrency = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        const currency = e.target.value;
        this.setState({currency});
        const {pathname} = window.location;
        goToRoute(`${pathname}?currency=${currency || ''}`);
    };

    setLootboxDetailPage = (value: boolean) => this.setState({isLootboxDetailPage: value});

    setCurrencyUSD = () => this.setState({currency: 'usd'});

    scrollToFilter = (forceScroll = false, instantaneous = false) => {
        const desktop = isDesktop();
        const scrollTarget = desktop
            ? this.popularGamesRef.current
            : this.casinoFilterRef.current;

        if (scrollTarget) {
            if (!desktop || window.scrollY < HEIGHT_FILTER || forceScroll === true) {
                requestAnimationFrame(() => {
                    window.scrollTo({
                        behavior: instantaneous ? 'auto' : 'smooth',
                        top: desktop
                            ? scrollTarget.offsetHeight
                            : scrollTarget.getBoundingClientRect().top + window.scrollY
                    });
                });
            }
        }
    };

    onChangeSearchInput = (value: string, needClearFindValue = false, needClearSavedValue = false) => {
        this.setState({searchInputValue: value});

        if (needClearFindValue) {
            this.setState({findValue: '', savedFindValue: this.state.findValue});
        }

        if (needClearSavedValue) {
            this.setState({savedFindValue: ''});
        }
    };

    onClickBackspace = (event: React.KeyboardEvent): void => {
        if (event.key !== 'Backspace') {
            return;
        }

        const {searchInputValue, savedFindValue} = this.state;

        if (searchInputValue === '') {
            this.clearSearch();

            if (savedFindValue === '') {
                event.preventDefault();
            }
        }
    };

    renderGridIframe = (): React.ReactNode => {
        const {casinoGamesStore} = this.props;
        const {isGameActive, activeCasinoGame, iframeGridPlacement} = casinoGamesStore;

        if (!isGameActive || !iframeGridPlacement?.rowStart) {
            return null;
        }

        return <div
            ref={this.iframeGridRef}
            className="casino-list-games__iframe"
            key="casino-grid-iframe"
            style={{gridRowStart: iframeGridPlacement.rowStart}}
            {...CASINO_ATTRS.IFRAME}
        >
            <IframeView activeCasinoGame={activeCasinoGame}/>
        </div>;
    };

    renderAllGames = (): React.ReactNode => {
        const {userStore, casinoGamesStore} = this.props;
        const {casinoGames} = casinoGamesStore.data;
        const {hasMore, selectedFilterGroupId, isGameActive, iframeGridPlacement} = casinoGamesStore;
        const {activeClickGame} = this.state;
        const {cool_off} = userStore.user;

        return <div className="casino-game-group">
            <div className="casino-list-games" {...CASINO_ATTRS.GRID}>
                {selectedFilterGroupId === ALL_GAMES_FILTER_ID && <CasinoMarketingBlocks
                    activeClickGame={activeClickGame}
                    biggestWins={casinoGamesStore.biggestWins ?? []}
                    cool_off={cool_off}
                    iframeGridPlacement={iframeGridPlacement}
                    isGameActive={isGameActive}
                    pageElements={casinoGamesStore.pageElements}
                    setActiveGame={this.setActiveGame}
                    deactivateGame={this.deactivateGame}
                    scrollToFilter={this.scrollToFilter}
                    onGoToTournamentGames={this.handleGoToTournamentGames}
                />}
                {casinoGames && casinoGames.map(casinoGame => <CasinoGameItem
                    key={`casinoGame-all-${casinoGame.id}`}
                    active={activeClickGame === casinoGame.id}
                    setActiveGame={this.setActiveGame}
                    deactivateGame={this.deactivateGame}
                    cool_off_present={isCoolOffActiveFor(cool_off, ['casino'])}
                    {...casinoGame}
                />)}
                {isDesktop() && this.renderGridIframe()}
                {hasMore && <div className="casino-list-games__lazy-trigger">
                    <LazyLoading show={hasMore} callback={this.loadMoreGames}/>
                </div>}
            </div>
        </div>;
    };

    render(): React.ReactNode {
        const {casinoGamesStore, userStore} = this.props;
        const {casino_disabled, cool_off} = userStore.user;

        if (casino_disabled) {
            return <DisabledCasino/>;
        }

        const {activeClickGame, findValue, key, isLootboxDetailPage} = this.state;
        const {
            casinoGames
        } = casinoGamesStore.data;
        const {loadingNewGames,
            showOnlyFavorite,
            sortings,
            sortingValue,
            selectedFilterGroupId,
            filters,
            isGameActive,
            activeCasinoGame,
            favoritesGameCount} = casinoGamesStore;
        const lootboxTabActive = selectedFilterGroupId === CASINO_TAB_LOOTBOX;
        const popularFilter = casinoGamesStore.popularGamesFilter;

        return <>
            <div
                className={`casino-page ${isLootboxDetailPage ? 'casino-lootbox-page' : ''}`}
                ref={this.rootElement}
            >
                <h1 className="visually-hidden-title">{I18n.t('menu_links.casino')}</h1>
                {<div ref={this.popularGamesRef}>
                    <PopularGamesCarousel
                        filter={popularFilter}
                        activeClickGame={activeClickGame}
                        setActiveGame={this.setActiveGame}
                        deactivateGame={this.deactivateGame}
                        cool_off_present={isCoolOffActiveFor(cool_off, ['casino'])}
                    />
                </div>}
                <div className="casino-filter" ref={this.casinoFilterRef}>
                    {lootboxTabActive
                        ? !this.state.isLootboxDetailPage && <div className="casino-filter__currency-lootboxes">
                            <div className="select-currency classical-select field__select">
                                <SelectPicker
                                    id="currency_select"
                                    onChange={this.handleChangeCurrency}
                                    value={this.state.currency}
                                    collection={this.currencies}
                                />
                            </div>
                        </div>
                        : <div className="casino-filter__section section-search">
                            <div className="classical-select games-select">
                                <span className="search-container__icon">
                                    <SpriteIcon name="#svg-search-icon" className="svg-default"/>
                                </span>
                                <LiveCreatableSelect
                                    keyForSelect={key}
                                    value={{icon: null, label: findValue, value: ELEMENT_NOT_FOUND}}
                                    placeholder={I18n.t('search')}
                                    loadOptions={this.loadOptions}
                                    customOption={this.customOption}
                                    onChange={this.changSearchData}
                                    isClearable
                                    isValue
                                    onClick={this.scrollToFilter}
                                    onChangeInput={this.onChangeSearchInput}
                                    inputValue={this.state.searchInputValue}
                                    onClickBackspace={this.onClickBackspace}
                                />
                            </div>
                            <ThirdLevelSort clearSearch={this.updatedSelect} scrollToFilter={this.scrollToFilter}/>
                        </div>}
                    <div className="casino-filter__section section-favourite">
                        <FilterGroups
                            casinoFilters={filters}
                            selectedFilterGroupId={selectedFilterGroupId}
                            onClick={this.clearSearch}
                            showOnlyFavorite={showOnlyFavorite}
                            favoritesCount={favoritesGameCount}
                            handleButtonFavouriteClick={this.favoriteFilter}
                        />
                        <FirstLevelSort
                            onClick={this.clearSearch}
                            sortings={sortings}
                            sortingValue={sortingValue}
                        />
                    </div>
                </div>
                {!isDesktop() && isGameActive &&
                    <IframeView activeCasinoGame={activeCasinoGame}/>}
                {loadingNewGames
                    ? <SmartLoader loaderClassName={'casino-game-loader'}/>
                    : <div className="casino-game-groups">
                        {lootboxTabActive &&
                            <CasinoLootboxes
                                currency={this.state.currency}
                                setCurrencyUSD={this.setCurrencyUSD}
                                isLootboxDetailPage={this.state.isLootboxDetailPage}
                                setLootboxDetailPage={this.setLootboxDetailPage}
                                scrollToFilter={this.scrollToFilter}
                            />}
                        {!lootboxTabActive && casinoGames && (casinoGames?.length > 0
                            ? this.renderAllGames()
                            : <EmptyGames favourite={favoritesGameCount === 0 && showOnlyFavorite}/>)}
                    </div>}
            </div>
            {!isDesktop() && isGuest() && <BarLoginMobile/>}
            {this.showScrollUp()}
        </>;
    }
}
